import { ReactElement, useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../context/theme.context';
import LineChart from '../LineChart';

type props = {
  deviceData: { value: number[][]; color: string; serailNumber: number }[];
  hideTicks?: boolean;
  dashed?: boolean;
  boxPoints: { start: Date | null; end: Date | null };
  scatterPoint: number;
  scatterPointLabel: string;
  scatterPointTime: Date | null;
};

const POINT_COLOR = '#D92D20';

/**
 * Graph component that renders a line chart using Chart.js and React-Chartjs-2.
 * @template T - The type of data values in the chart.
 * @param props - The component props.
 * @param  props.deviceData - An array of data values to be displayed on the chart.
 * @param  props.hideTicks - Show or Hide ticks.
 * @param  props.dashed - Dashed grid.
 * @param  props.boxPoints - Points on graph for box construction.
 * @param  props.scatterPoint - Scatter Point.
 * @param  props.scatterPointLabel - Scatter Point Label.
 * @param  props.scatterPointTime - Scatter Point Time.
 * @returns  A React element representing the Graph component.
 */
export default function AlertGraph({
  deviceData,
  hideTicks,
  dashed,
  boxPoints,
  scatterPoint,
  scatterPointLabel,
  scatterPointTime
}: props): ReactElement {
  // TODO:have to figure out a way to pass the tailwindcss' color classes for colors in levelcolor,gridcolor.
  const [levelcolor, setLevelColor] = useState('#DOD5DD');
  const [gridcolor, setGridColor] = useState('#F2F4F7');
  const { isDarkTheme } = useContext(ThemeContext);
  const [graphData, setGraphData] = useState<any[]>([]);

  useEffect(() => {
    const datasetsArray: any[] = [];
    deviceData.forEach((device) => {
      const graphDatasetValuesPerParameter: any = {
        label: device.serailNumber,
        tension: 0.2,
        borderColor: device.color,
        backgroundColor: device.color,
        pointRadius: 0,
        data: device.value,
        order: 1
      };

      datasetsArray.push(graphDatasetValuesPerParameter);
      if (scatterPointTime) {
        datasetsArray.push({
          label: scatterPointLabel,
          data: [[scatterPointTime, scatterPoint]],
          borderColor: POINT_COLOR,
          pointBorderWidth: 5,
          pointRadius: 6.25,
          pointBackgroundColor: POINT_COLOR, //
          pointBorderColor: POINT_COLOR,
          order: 0
        });
      }
    });
    setGraphData([...datasetsArray]);
  }, [deviceData, scatterPoint]);

  useEffect(() => {
    if (isDarkTheme) {
      setLevelColor('#475467');
      setGridColor('#101828');
    } else {
      setLevelColor('#D0D5DD');
      setGridColor('#F2F4F7');
    }
  }, [isDarkTheme]);

  const data = {
    datasets: graphData
  };

  const options: any = {
    responsive: true,
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    hover: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false,
        position: 'top' as const
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        type: 'timeseries',
        time: {
          unit: 'minute',
          displayFormats: {
            minute: 'Do MMMM hh:mm a'
          }
        },
        grid: {
          display: false
        },
        ticks: {
          color: levelcolor,
          maxTicksLimit: 5,
          display: !hideTicks,
          source: 'data'
        },
        beginAtZero: false
      },
      y: {
        grid: {
          display: true,
          color: gridcolor
        },
        border: {
          dash: !dashed ? [0, 0] : [4, 2]
        },
        beginAtZero: false,
        ticks: {
          color: levelcolor,
          display: !hideTicks
        }
      }
    },
    maintainAspectRatio: false
  };

  if (boxPoints.start) {
    options.plugins.annotation = {
      annotations: {
        box1: {
          // Indicates the type of annotation
          type: 'box',
          xMin: boxPoints.start,
          xMax: boxPoints.end,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 0,
          z: -1
        }
      }
    };
  }

  if (graphData.length > 0 && graphData[0].data[0]) {
    options.scales.x.min = new Date(graphData[0].data[0][0]);
  }
  return (
    <div className="text-gray-500 w-full h-full">
      <LineChart options={options} data={data} />
    </div>
  );
}
